/**
 * #2901 — `agent.history@1` cursor extraction + shape validation.
 *
 * Pure + React-free (so the crucible-gui contract-pin can import it into a
 * runtime-boot test without pulling the chat hook's module graph). The client
 * stores a cursor ONLY when it is shape-valid; anything else becomes `undefined`
 * so the next request is cold (via `shouldFetchCold`'s `!hasCursor`).
 *
 * The validation MIRRORS the runtime's `parseAgentHistoryCursor`
 * (`crucible/src/runtime/jsonrpc-methods.ts`) EXACTLY (#2901 Codex r5/r6): a
 * cursor must be a non-array object with a non-array object `perFile`, every
 * lane a non-null non-array object, and every seq a finite number. Without the
 * full mirror, a shape the guard accepted but the runtime rejects (e.g. an array
 * lane, or a string/NaN/Infinity seq) would be forwarded on the next warm poll,
 * `agent.history@1` would reject it (-32602), and on the cookie-mode SPA path
 * that rejection is caught as `null` BEFORE the accumulator commits — so the
 * poisoned cursor is never cleared and warm polls wedge until the 30s reconcile.
 * The `crucible-gui` contract-pin asserts guard⊆parser equivalence against a real
 * server, so this mirror can't silently drift.
 */
import type { AgentHistoryCursorLike } from '@volley/crucible-ipc-types/ipc-channels';

/**
 * True iff `cursor` is a well-formed `AgentHistoryCursor` the runtime parser
 * would accept: `{ perFile: { <lane>: { <deviceId>: <finite number> } } }`, all
 * containers non-null non-array objects. `perFile: {}` (empty lanes) is valid.
 */
export function isValidHistoryCursor(cursor: unknown): cursor is AgentHistoryCursorLike {
  if (!cursor || typeof cursor !== 'object' || Array.isArray(cursor)) return false;
  const perFile = (cursor as { perFile?: unknown }).perFile;
  if (typeof perFile !== 'object' || perFile === null || Array.isArray(perFile)) return false;
  for (const lane of Object.values(perFile as Record<string, unknown>)) {
    if (typeof lane !== 'object' || lane === null || Array.isArray(lane)) return false;
    for (const seq of Object.values(lane as Record<string, unknown>)) {
      if (typeof seq !== 'number' || !Number.isFinite(seq)) return false;
    }
  }
  return true;
}

/**
 * Extract the opaque cursor from an `agent.history@1` page, returning it only
 * when shape-valid (see {@link isValidHistoryCursor}). Absent / malformed / the
 * desktop handler's fail-safe empty page (`cursor: {}`, no `perFile`) ⇒
 * `undefined` ⇒ the next request is cold.
 */
export function extractHistoryCursor(page: unknown): AgentHistoryCursorLike | undefined {
  if (!page || typeof page !== 'object') return undefined;
  const cursor = (page as { cursor?: unknown }).cursor;
  return isValidHistoryCursor(cursor) ? cursor : undefined;
}
